/**
 * Loopback hosts, as `URL.hostname` renders them (IPv6 keeps its brackets).
 * RFC 8252 §7.3: a native client may receive its redirect on a loopback
 * listener over plain `http:`, because the loopback interface is not reachable
 * from the network.
 */
export const OAUTH_LOOPBACK_HOSTS: ReadonlySet<string> = new Set([
  '127.0.0.1',
  '[::1]',
  'localhost',
]);

/**
 * Schemes that are never an OAuth client's callback. Private-use schemes are
 * open-ended (RFC 8252 §7.1), so this is a blocklist, and each entry is
 * here for one of three reasons:
 *
 * - Content or script. The demonstrated attack is the web org picker: its
 *   `location.assign('javascript:…')` runs the script in the picker's own
 *   document, on the app origin. The server's 302s do not share that risk
 *   (browsers abort a redirect to `javascript:`, and modern browsers block
 *   top-level `data:` navigation and give `data:` an opaque origin), so the
 *   rest of this family is defense in depth, not a known exploit path.
 * - Remote fetch: it pulls a file or share from an attacker-chosen host
 *   (`ftp:`, `smb:`, …).
 * - Selected operating-system or browser handlers: a redirect from our origin
 *   would launch a system tool or browser internal behind a trusted sign-in
 *   (`search-ms:`, `shell:`, …; the `ms-` family is in
 *   {@link BLOCKED_SCHEME_PREFIXES}). This covers known-abused handlers only,
 *   not the whole category: other native schemes (`telnet:`, `afp:`,
 *   `x-apple.systempreferences:`, …) still pass, and whether they do anything
 *   depends on what the user has installed.
 *
 * Strict RFC 8252 §7.1 (reverse-domain schemes only) is not enforced: real
 * MCP clients register `cursor:` and `vscode:`. A positive native-callback
 * policy with documented exceptions for those would close the handler gap.
 */
const BLOCKED_SCHEMES: ReadonlySet<string> = new Set([
  // Content or script.
  'javascript:',
  'livescript:',
  'mocha:',
  'vbscript:',
  'data:',
  'blob:',
  'about:',
  'filesystem:',
  'view-source:',
  'mhtml:',
  'jar:',
  'res:',
  'resource:',
  'chrome:',
  'chrome-extension:',
  'moz-extension:',
  'intent:',
  // Remote or local file fetch.
  'file:',
  'ftp:',
  'ftps:',
  'sftp:',
  'smb:',
  'nfs:',
  'webdav:',
  'ws:',
  'wss:',
  // Operating-system handlers.
  'search:',
  'search-ms:',
  'shell:',
  'mk:',
  'its:',
]);

/**
 * Scheme families owned by an operating-system vendor, where individual
 * handlers (`ms-msdt:`, `ms-officecmd:`, `microsoft-edge:`, …) keep being
 * added and abused.
 */
const BLOCKED_SCHEME_PREFIXES: readonly string[] = ['ms-', 'microsoft-'];

function isBlockedScheme(protocol: string): boolean {
  return (
    BLOCKED_SCHEMES.has(protocol) ||
    BLOCKED_SCHEME_PREFIXES.some((prefix) => protocol.startsWith(prefix))
  );
}

/** Why a redirect URI was refused. Stable, so callers can branch or log it. */
export type OAuthRedirectUriRejection =
  | 'unparseable'
  | 'whitespace_or_control'
  | 'fragment'
  | 'blocked_scheme'
  | 'insecure_http';

export type OAuthRedirectUriCheck = { ok: true } | { ok: false; reason: OAuthRedirectUriRejection };

// Space, C0 controls, and DEL. The WHATWG parser silently strips or skips
// these, so a string carrying them can parse to a different URI than it reads
// as; refusing them keeps "the stored string" and "where we navigate" equal.
// eslint-disable-next-line no-control-regex
const WHITESPACE_OR_CONTROL = /[\u0000- \u007f]/;

/**
 * Whether `uri` is safe to register and later redirect a browser to as an
 * OAuth client redirect URI.
 *
 * - `https:` on any host.
 * - `http:` only on a loopback host (RFC 8252 §7.3).
 * - Private-use custom schemes such as `cursor:` or `vscode:` (RFC 8252 §7.1),
 *   except the content, fetch, and selected system-handler schemes in
 *   {@link BLOCKED_SCHEMES} and {@link BLOCKED_SCHEME_PREFIXES}.
 * - Never a fragment (RFC 6749 §3.1.2), never surrounding or embedded
 *   whitespace or control characters, and it must parse with `new URL`.
 *
 * The parsed `protocol` is lower-cased by the URL parser, so case variants of
 * a blocked scheme are refused too.
 */
export function checkOAuthRedirectUri(uri: string): OAuthRedirectUriCheck {
  if (WHITESPACE_OR_CONTROL.test(uri)) return { ok: false, reason: 'whitespace_or_control' };
  // Checked on the raw string: `new URL('https://a/cb#').hash` is empty, so an
  // empty fragment would slip past a parsed check.
  if (uri.includes('#')) return { ok: false, reason: 'fragment' };
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return { ok: false, reason: 'unparseable' };
  }
  if (isBlockedScheme(url.protocol)) return { ok: false, reason: 'blocked_scheme' };
  if (url.protocol === 'http:' && !OAUTH_LOOPBACK_HOSTS.has(url.hostname)) {
    return { ok: false, reason: 'insecure_http' };
  }
  return { ok: true };
}

/** Boolean form of {@link checkOAuthRedirectUri}. */
export function isSafeOAuthRedirectUri(uri: string): boolean {
  return checkOAuthRedirectUri(uri).ok;
}
